import { ConfirmationDialog, PoolFormattedMessage } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as TagActions from '../../../../common/actions/tag-actions';
import { ConfigurationTag } from './models/configuration-tag';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface TagsOverviewProps {
    onEdit: (unit: ConfigurationTag) => void;
}

const columnsOf = (): PaloColumn<ConfigurationTag>[] => [
    {
        key: 'name',
        type: 'name',
        head: <FormattedMessage id="app.table.column.tag" />,
        cell: tag => tag.name,
    },
    {
        key: 'description',
        type: 'name',
        head: <FormattedMessage id="app.table.column.description" />,
        cell: tag => tag.description,
    },
];

export const TagsOverview = (props: TagsOverviewProps) => {
    const dispatch = useThunkDispatch();
    const [deleteModalData, setDeleteModalData] = useState<ConfigurationTag | null>(null);
    const tags = useAppSelector(state => state.tag.configurationTags);
    const [searchParams, setSearchParams] = useSearchParams();

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    useEffect(() => {
        dispatch(TagActions.fetchConfigurationTags(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (!deleteModalData) {
            return;
        }

        dispatch(TagActions.deleteConfigurationTag(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<ConfigurationTag, number>
                embedded={true}
                columns={columnsOf()}
                items={tags.items}
                getRowId={tag => tag.id}
                emptyLabel="Keine Tags."
                rowActions={tag => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(tag) },
                    { label: <FormattedMessage id="app.common.delete" />, onClick: () => setDeleteModalData(tag) },
                ]}
                pagination={{
                    count: tags.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />
            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.configuration.deleteTagModal" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleDelete}
                onCancel={() => setDeleteModalData(null)}
            >
                <PoolFormattedMessage
                    id="app.configuration.deleteTagModalText"
                    values={{ name: deleteModalData?.name }}
                />
            </ConfirmationDialog>
        </>
    );
};
